import {
  Component,
  Input,
  OnInit,
  Signal,
  effect,
  inject,
} from "@angular/core";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { User } from "firebase/auth";
import { StateModule } from "projects/shared-state/lib/state.module";
import { StateService } from "projects/shared-state/lib/state.service";
import { Workflow } from "projects/shared-state/lib/types/types";
import { WorkflowState } from "projects/shared-state/public-api";
import { MatIconModule } from "@angular/material/icon";
import { MatDialog } from "@angular/material/dialog";
import { PostgenieProcessComponent } from "../prompt-process/postgenie-process.component";
import { CommonModule } from "@angular/common";
import { MatTabsModule } from "@angular/material/tabs";
import { ResultEditingComponent } from "./result-editing/result-editing.component";
import { ResultReviewComponent } from "./result-review/result-review.component";
import { MatTooltipModule } from "@angular/material/tooltip";

@Component({
  standalone: true,
  selector: "postgenie-result-process",
  templateUrl: "./result-process.component.html",
  styleUrls: ["./result-process.component.scss"],
  imports: [
    CommonModule,
    StateModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTabsModule,
    ResultEditingComponent,
    ResultReviewComponent,
    MatTooltipModule,
  ],
})
export class ResultProcessComponent implements OnInit {
  @Input() user: User;
  dialog = inject(MatDialog);
  stateService = inject(StateService);
  activeRouter = inject(ActivatedRoute);
  router = inject(Router);

  stateSignal$: Signal<{
    workflow: WorkflowState;
  }> = this.stateService.getAppState();

  public disablePage$ = true;
  workflowId: string = "";
  isInactiveWorkflow: boolean = false;

  step: number = 0;

  workflowForm: FormGroup = new FormGroup({
    postName: new FormControl("", []),
  });
  nameEditing: boolean = false;

  items = [
    {
      title: "Be mindful while tagging",
      description:
        "Tag a maximum of 10 people or companies in your post. Ensure that they react to your post within the first hour after posting.",
      expanded: true,
    },
    {
      title: "Maintain a regular posting schedule",
      description:
        "Consistency is key for establishing a strong presence on LinkedIn. Keep your audience engaged and interested in your content.",
      expanded: true,
    },
    {
      title: "Interact with your network",
      description:
        "Engage with your connections by encouraging them to interact with your posts via a call-to-action and by liking and commenting on their posts. This will help foster relationships and boost your visibility on LinkedIn.",
      expanded: true,
    },
    {
      title: "Republish your content",
      description:
        "Since many people don't frequently check LinkedIn, republishing a post can enhance your reach and engagement.",
      expanded: true,
    },
    {
      title: "Share others' content",
      description:
        "Increase your visibility by resharing posts from other individuals.",
      expanded: true,
    },
  ];

  constructor() {
    this.workflowId =
      this.activeRouter.snapshot.queryParamMap.get("workflow_id");
    if (this.workflowId !== this.stateSignal$().workflow.workflow.uid) {
      this.stateService.getWorkflow(this.workflowId);
    }
    effect(() => {
      const workflow: Workflow = this.stateSignal$().workflow.workflow;
      this.workflowForm.patchValue({
        postName: workflow.name,
      });
      if (
        this.stateSignal$().workflow.workflow.promptVersion !== "" &&
        this.stateSignal$().workflow.subscription.promptVersion !== ""
      ) {
        if (
          this.stateSignal$().workflow.workflow.promptVersion !==
          this.stateSignal$().workflow.subscription.promptVersion
        ) {
          this.step = 1;
          this.isInactiveWorkflow = true;
        } else if (
          this.stateSignal$().workflow.workflow.promptVersion ===
          this.stateSignal$().workflow.subscription.promptVersion
        ) {
          this.isInactiveWorkflow = false;
        }
      }
    });
  }

  ngOnInit(): void {
    this.stateService.geSubscriptionApi();
  }

  openPostSettings(): void {
    const dialogRef = this.dialog.open(PostgenieProcessComponent, {
      width: "80%",
      height: "80%",
      data: {
        workflowId: undefined, // Takes current workflow if workflowId is undefined
      },
    });
    dialogRef.afterClosed().subscribe(() => {
      if (this.stateSignal$().workflow.workflow.status !== "review") {
        this.navigateTo("home");
      }
    });
  }

  navigateTo(route: string, queryParam: Object = {}): void {
    this.router.navigate([route], {
      queryParams: queryParam,
      queryParamsHandling: "merge",
    });
  }

  nextStep(): void {
    this.step = (this.step + 1) % 2;
  }

  toggle(item: any) {
    item.expanded = !item.expanded;
  }

  changePostName(value) {
    this.nameEditing = value;
    if (!value) {
      if (
        this.workflowForm.value.postName !==
        this.stateSignal$().workflow.workflow.name
      ) {
        this.saveWorkflow();
      }
    }
  }

  copyToClipboard() {
    let text = document.getElementById("post-content").innerHTML;
    function listener(e) {
      e.clipboardData.setData("text/html", text);
      e.clipboardData.setData("text/plain", text);
      e.preventDefault();
    }
    document.addEventListener("copy", listener);
    if (!navigator.clipboard) {
      if (text) {
        document.execCommand("copy");
      }
    } else {
      navigator.clipboard.write([
        new ClipboardItem({
          "text/plain": new Blob([text], { type: "text/plain" }),
          "text/html": new Blob([text], { type: "text/html" }),
        }),
      ]);
    }
    document.removeEventListener("copy", listener);
  }

  saveWorkflow(): void {
    this.stateService.saveWorkflow(
      {
        ...this.stateSignal$().workflow.workflow,
        name: this.workflowForm.value.postName,
      },
      this.stateSignal$().workflow.workflow.uid,
    );
  }
}
